/**
 * The periodic "I am still here" a nominated computer sends.
 *
 * Presence is derived from this and nothing else, so it has to keep running on
 * every page the operator might be sitting on — not only the ones the React
 * client owns. That is why it has its own entrypoint in the layout.
 *
 * It also carries what an administrator needs to see about a machine they
 * cannot walk up to: which build it is running, whether it believes itself
 * offline-ready, how far its clock has drifted, and how much unsynchronised
 * work it is holding.
 */
import { ApiError } from "@/app/api-client"
import { DEVICE_PATHS, signedRequest } from "./client"
import type { DeviceIdentity, HeartbeatResponse } from "./identity"
import { clearIdentity, readIdentity, updateCredential } from "./store"

/** Reasons the server has disowned this machine. Its stored identity is worthless afterwards. */
const DISOWNING_CODES = new Set(["device_revoked", "device_not_found", "device_not_enrolled"])

export interface HeartbeatReport {
  appVersion?: string
  indexeddbSchemaVersion?: number
  syncProtocolVersion?: string
  authorizationSnapshotVersion?: number
  offlineReady?: boolean
  offlineReadyReason?: string
  pendingMutationCount?: number
  /**
   * When the oldest thing still in the outbox happened. Eleven changes made ten
   * minutes ago is a computer working through a dropout; one change from nine
   * days ago is a shift that never left, on a machine whose small number makes
   * it look fine (§26).
   */
  oldestPendingOccurredAt?: string
}

export interface HeartbeatOptions {
  /** Everything the phases after this one will have an answer for and this one does not. */
  report?: () => HeartbeatReport | Promise<HeartbeatReport>
  onResponse?: (response: HeartbeatResponse) => void
  onError?: (error: unknown) => void
  onDisowned?: () => void
}

export interface HeartbeatHandle {
  stop: () => void
  /** Sends one now, outside the schedule. Used when the tab becomes visible again. */
  beat: () => Promise<void>
}

/**
 * Milliseconds between this computer's clock and the server's, as of the last
 * heartbeat. Reported back on the next one, which is how an administrator finds
 * out about a drifting clock before it starts refusing offline sign-ins.
 */
let clockOffsetMs: number | null = null

export function lastClockOffsetMs(): number | null {
  return clockOffsetMs
}

export async function startHeartbeat(options: HeartbeatOptions = {}): Promise<HeartbeatHandle | null> {
  const identity = await readIdentity()
  if (!identity) return null

  let timer: ReturnType<typeof setTimeout> | undefined
  let stopped = false
  let inFlight = false

  const beat = async () => {
    // One at a time. A tab that has been backgrounded fires its catch-up beat
    // just as the scheduled one lands, and two heartbeats racing means two
    // rows in the nonce table for no information gained.
    if (stopped || inFlight) return
    inFlight = true

    try {
      const response = await sendHeartbeat(identity, options)
      options.onResponse?.(response)
      schedule(response.heartbeatIntervalSeconds * 1000)
    } catch (error) {
      if (error instanceof ApiError && DISOWNING_CODES.has(error.code)) {
        await clearIdentity()
        stopped = true
        options.onDisowned?.()
        return
      }

      options.onError?.(error)
      // Being unable to reach the server is the normal state this whole
      // feature exists for, so a failure retries on the ordinary schedule
      // rather than backing off into silence.
      schedule(DEFAULT_INTERVAL_MS)
    } finally {
      inFlight = false
    }
  }

  const schedule = (delay: number) => {
    if (stopped) return
    clearTimeout(timer)
    timer = setTimeout(beat, delay)
  }

  const onVisible = () => {
    if (document.visibilityState === "visible") void beat()
  }

  document.addEventListener("visibilitychange", onVisible)
  void beat()

  return {
    beat,
    stop: () => {
      stopped = true
      clearTimeout(timer)
      document.removeEventListener("visibilitychange", onVisible)
    }
  }
}

const DEFAULT_INTERVAL_MS = 30_000

async function sendHeartbeat(identity: DeviceIdentity, options: HeartbeatOptions): Promise<HeartbeatResponse> {
  const reported = (await options.report?.()) ?? {}
  const sentAt = Date.now()

  const response = await signedRequest<HeartbeatResponse>(identity, "POST", DEVICE_PATHS.heartbeat, {
    ...reported,
    clockOffsetMs: clockOffsetMs,
    storageHealth: await storageHealth()
  })

  // Measured against the moment the request left rather than the moment the
  // answer arrived, so the round trip is not counted as drift.
  clockOffsetMs = sentAt - Date.parse(response.serverTime)

  await updateCredential(response.credential)

  return response
}

/**
 * Whether the browser has promised not to evict this origin's data, and how
 * much room is left. Both are readiness conditions in the phase that follows,
 * and both are worth an administrator seeing before then.
 */
async function storageHealth(): Promise<Record<string, unknown>> {
  if (typeof navigator === "undefined" || !navigator.storage) return {}

  try {
    const estimate = (await navigator.storage.estimate?.()) ?? {}

    return {
      persisted: (await navigator.storage.persisted?.()) ?? false,
      quotaBytes: estimate.quota ?? null,
      usageBytes: estimate.usage ?? null
    }
  } catch {
    return {}
  }
}
